import { useLoading } from '@teko/ds-desktop';
import type { ProfileUpdate, RequiredProfileInformation } from '@teko/user';
import { useServices } from 'contexts';
import { parseISO } from 'date-fns';
import { isValidEmailFormat, isValidPhoneFormat } from '@teko/utils';
import { cleanObject } from '@teko/utils';
import { t } from 'i18n';
import { useCallback, useEffect, useState } from 'react';
import { logger } from '@teko/log';
import { useAuth } from '@teko/auth-react';
import { ApiError } from '@teko/api';

const TOTAL_COUNTDOWN = 60;
export interface OTPData {
  telephone?: string;
  email?: string;
}
export type ProfileFeedback = Partial<Record<keyof ProfileUpdate, string>>;
interface UseUploadProfileProp {
  customRequireFields?: RequiredProfileInformation;
  uploadProfile: (payload: ProfileUpdate) => Promise<unknown>;
  verifyPhone?: (telephone: string, OTP: string) => Promise<unknown>;
  verifyEmail?: (email: string, OTP: string) => Promise<unknown>;
}

/**
 * upload/update profile hook
 * @param props ignore verifyPhone or verifyEmail also ignore OTP verifying for that field.
 */
export const useUploadProfile = ({
  customRequireFields = {},
  uploadProfile,
  verifyPhone,
  verifyEmail,
}: UseUploadProfileProp) => {
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout>();
  const { authService, userService } = useServices();
  const [loading, setLoading] = useLoading();
  const [countdown, setCountdown] = useState(0);
  const [otpData, setOTPData] = useState<OTPData | null>(null);
  const [otpError, setOTPError] = useState<string | null>(null);
  const [message, setMessage] = useState<{
    success: boolean;
    content: string;
  }>({
    success: false,
    content: '',
  });
  const [feedbacks, setFeedbacks] = useState<ProfileFeedback>({});
  const [allowCustomerEditProfile, setAllowCustomerEditProfile] =
    useState<boolean>(true);
  const [requiredFields, setRequiredFields] =
    useState<RequiredProfileInformation>(customRequireFields);
  const { user: profile } = useAuth();

  const shouldVerifyEmail = !!verifyEmail;
  const shouldVerifyTelephone = !!verifyPhone;
  const waitingVerifyOTP =
    !!otpData && Object.values(otpData).some((value) => !!value);

  useEffect(() => {
    userService
      .getClient()
      .then((clients) => {
        const { requiredProfileInformation, customerCanEditProfile } =
          clients[0];
        setAllowCustomerEditProfile(
          customerCanEditProfile === false ? false : true,
        );
        if (!requiredProfileInformation) return;
        const { phone, ...others } = requiredProfileInformation;
        setRequiredFields({
          telephone: phone,
          ...others,
          ...customRequireFields,
        });
      })
      .catch((error) => logger.error(error));
  }, [userService]);

  const setSuccessMessage = (msg: string) =>
    setMessage({ success: true, content: msg });
  const setFailureMessage = (msg: string) =>
    setMessage({ success: false, content: msg });

  const resetStates = () => {
    setMessage({ success: true, content: '' });
    setFeedbacks({});
    setOTPError(null);
    setOTPData(null);
    setCountdown(0);
    clearInterval(intervalId);
  };
  const validate = useCallback(
    (data: ProfileUpdate) => {
      const validationErrors: ProfileFeedback = {};
      // check for required values
      Object.entries(requiredFields).map(([field, isRequired]) => {
        if (
          isRequired &&
          Object.keys(data).includes(field) &&
          !data[field as keyof ProfileUpdate]
        ) {
          validationErrors[field as keyof ProfileFeedback] = t(
            `Vui lòng nhập thông tin`,
          );
        }
      });
      if (data.telephone && !isValidPhoneFormat(data.telephone)) {
        validationErrors.telephone = t(
          'Vui lòng nhập số điện thoại đúng định dạng',
        );
      }
      if (data.email && !isValidEmailFormat(data.email)) {
        validationErrors.email = t('Vui lòng nhập email đúng định dạng');
      }
      if (data.dob) {
        const parsedDob = parseISO(data.dob);
        if (parsedDob > new Date() || parsedDob < new Date('1/1/1900')) {
          validationErrors.dob = t('Ngày sinh không hợp lệ');
        }
      }
      setFeedbacks(validationErrors);
      return validationErrors;
    },
    [requiredFields],
  );

  const submit = useCallback(
    async (data: ProfileUpdate) => {
      try {
        const payload: ProfileUpdate = {};
        // only send changed fields
        Object.entries(cleanObject(data)).forEach(([key, value]) => {
          if (
            data[key as keyof ProfileUpdate] !=
            profile?.[key as keyof ProfileUpdate]
          ) {
            payload[key as keyof ProfileUpdate] = value;
          }
        });
        const errors = validate(payload);
        if (
          Object.keys(payload).length > 0 &&
          Object.keys(errors).length <= 0
        ) {
          setLoading(true);
          const res = await uploadProfile(payload);
          setSuccessMessage(t('Cập nhật thông tin người dùng thành công'));
          return res;
        }
        return null;
      } catch (e) {
        if (
          e instanceof ApiError &&
          e.response?.hasOwnProperty('schema_errors')
        ) {
          setFailureMessage(t('Thông tin không hơp lệ'));
          const errors: ProfileFeedback = {};
          const schemaErrors: Record<string, string[]> =
            e.response.schema_errors;
          // join every error per field into one string splitted by \n character
          Object.entries(schemaErrors).forEach(([key, value]) => {
            errors[key as keyof ProfileFeedback] = value.join('\n');
          });
          setFeedbacks(errors);
        } else {
          setFailureMessage(t('Đã có lỗi xảy ra!'));
        }
      } finally {
        setLoading(false);
      }
    },
    [validate, profile, setLoading, uploadProfile],
  );

  const sendOTP = useCallback(
    async (data: Pick<ProfileUpdate, 'email' | 'telephone'>) => {
      try {
        const errors = validate(data);
        if (Object.keys(errors).length > 0) return;

        const { email, telephone } = data;
        const otpType =
          telephone && shouldVerifyTelephone
            ? 'telephone'
            : email && shouldVerifyEmail
            ? 'email'
            : null;
        if (!otpType) return;
        setLoading(true);
        const verifyPromise: Promise<void> =
          otpType === 'telephone'
            ? authService.verifyPhone(telephone ?? '')
            : authService.verifyEmail(email ?? '');

        await verifyPromise;
        setOTPData(data);
        // Init timer allow resending otp after 60s
        setCountdown(TOTAL_COUNTDOWN);
        const handle = setInterval(() => {
          setCountdown((countdown) =>
            countdown === 0 ? countdown : countdown - 1,
          );
        }, 1000);
        setIntervalId(handle);
      } catch (err) {
        setFeedbacks((prevFeedbacks) => ({
          ...prevFeedbacks,
          [data?.email ? 'email' : 'telephone']:
            err instanceof ApiError ? authService.errorMsg(err) : err,
        }));
      } finally {
        setLoading(false);
        return false;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [authService, setLoading, validate],
  );

  const update = useCallback(
    async (data: ProfileUpdate) => {
      resetStates();
      try {
        if (data.telephone && shouldVerifyTelephone) {
          await sendOTP({ telephone: data.telephone });
          return null;
        } else if (data.email && shouldVerifyEmail) {
          await sendOTP({ email: data.email });
          return null;
        } else return submit(data);
      } catch (err) {
        setFailureMessage(t('Đã có lỗi xảy ra!'));
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [resetStates, sendOTP, setLoading, submit],
  );
  const resendOTP = async () => {
    if (otpData) {
      setMessage({ success: true, content: '' });
      setOTPError(null);
      setCountdown(0);
      clearInterval(intervalId);
      await sendOTP(otpData);
    }
  };

  const verifyOtp = useCallback(
    async (OTP: string, data: ProfileUpdate) => {
      try {
        if (waitingVerifyOTP && OTP.length === 6) {
          setLoading(true);
          const { email, telephone } = otpData ?? {};
          // otpData should only include telephone or email at the same time,
          // otherwise it could update some unexpected field directly into user service.

          if (telephone && shouldVerifyTelephone) {
            await verifyPhone(telephone, OTP);
            delete data.telephone;
          } else if (email && shouldVerifyEmail) {
            await verifyEmail(email, OTP);
            delete data.email;
          }

          resetStates();
          setOTPData(null);
          setSuccessMessage(t('Cập nhật thành công'));
          // update other profile.
          submit(data);
        }
      } catch (err) {
        if (err instanceof ApiError) {
          setOTPError(authService.errorMsg(err));
        }
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      setLoading,
      otpData,
      resetStates,
      submit,
      verifyPhone,
      verifyEmail,
      authService,
    ],
  );

  return {
    waitingVerifyOTP,
    loading,
    requiredFields,
    allowEdit: allowCustomerEditProfile,
    otpError,
    otpData,
    setOTPData,
    message,
    feedbacks,
    countdown,
    resendOTP,
    resetStates,
    validate,
    update,
    verifyOtp,
  };
};
